import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, LayoutDashboard } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import type { ReportedIssue } from '@/types';

const statusColors = {
  new: 'bg-blue-50 text-blue-700 ring-blue-700/10',
  'in review': 'bg-yellow-50 text-yellow-700 ring-yellow-700/10',
  'on hold': 'bg-orange-50 text-orange-700 ring-orange-700/10',
  resolved: 'bg-green-50 text-green-700 ring-green-700/10',
  rejected: 'bg-red-50 text-red-700 ring-red-700/10',
} as const;

export default function TrackIssue() {
  const navigate = useNavigate();
  const [issueNumber, setIssueNumber] = useState('');
  const [issue, setIssue] = useState<ReportedIssue | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = async () => {
    if (!issueNumber.trim()) return;

    setIsSearching(true);
    try {
      const { data, error } = await supabase
        .from('sms_reported_issues')
        .select(`
          *,
          school:sms_schools(name),
          grade:sms_grades(name)
        `)
        .eq('issue_number', issueNumber.trim())
        .single();

      if (error) throw error;
      setIssue(data);
    } catch (error) {
      console.error('Error searching for issue:', error);
      toast.error('Issue not found');
      setIssue(null);
    } finally {
      setIsSearching(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-[url('https://images.unsplash.com/photo-1577896851231-70ef18881754')] bg-cover bg-center">
      <div className="min-h-screen bg-black/50">
        {/* Navigation */}
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => navigate('/admin')}
              className="bg-white hover:bg-gray-100 text-gray-900"
            >
              <LayoutDashboard className="mr-2 h-4 w-4" />
              Go to Dashboard
            </Button>
          </div>
        </div>

        {/* Hero Section */}
        <div className="container mx-auto px-4 pt-16 pb-12 text-center text-white">
          <h1 className="text-4xl font-bold tracking-tight mb-4">
            Report a Teacher Not Teaching Issue
          </h1>
          <p className="text-xl opacity-90 max-w-2xl mx-auto mb-8">
            Help us maintain high educational standards by reporting concerns professionally and confidentially.
          </p>
          <Button
            variant="outline"
            size="lg"
            className="bg-white hover:bg-gray-100 text-gray-900"
            onClick={() => navigate('/')}
          >
            Submit Report
          </Button>
        </div>

        {/* Track Issue Section */}
        <div className="container max-w-2xl mx-auto px-4 pb-16">
          <Card className="bg-white/95 backdrop-blur">
            <CardHeader>
              <CardTitle className="text-center">Track Your Report</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <Input
                    value={issueNumber}
                    onChange={(e) => setIssueNumber(e.target.value)}
                    placeholder="Enter issue number"
                    className="flex-1"
                  />
                  <Button onClick={handleSearch} disabled={isSearching}>
                    <Search className="h-4 w-4 mr-2" />
                    Track
                  </Button>
                </div>

                {issue && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <code className="text-sm font-semibold">
                        Issue #{issue.issue_number}
                      </code>
                      <Badge
                        variant="outline"
                        className={`${statusColors[issue.status as keyof typeof statusColors]}`}
                      >
                        {issue.status}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm font-medium text-muted-foreground">School:</span>
                        <p className="mt-1">{issue.school?.name}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-muted-foreground">Grade:</span>
                        <p className="mt-1">{issue.grade?.name}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-muted-foreground">Reported On:</span>
                        <p className="mt-1">{formatDate(issue.created_at!)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}